/**
 * @file AssetReadOnlyPanel.js
 * @description This panel displays the static read-only details associated with a Private asset.
 */

import { useEffect, useState } from 'react';
import { Avatar } from '@mui/material';
import { formatDateToMMDDYYYY } from '../../helpers/DateUtils';
import styles from '../AssetDrawer.module.scss';
import { useAuthContext } from '../../../store/Contexts';
import { getIndividualRecord } from '../../../services/individualService';
import { getText } from '../../../i18n';

const AssetReadOnlyPanel = ({ asset }) => {
    const { isLoggedIn, user, multitenantFlag } = useAuthContext();
    const [sharedIndividuals, setSharedIndividuals] = useState([]); // State to store names and avatar URLs

    const fetchIndividualFullName = async individualId => {
        try {
            const data = await getIndividualRecord(individualId);
            if (
                !data ||
                typeof data.firstName === 'undefined' ||
                typeof data.lastName === 'undefined'
            ) {
                return { name: 'Unknown Individual', avatarURL: null }; // Handle error case
            }
            return {
                name: data.tenantUsers[0].fullName,
                avatarURL: data.tenantUsers[0].avatarUrl,
            };
        } catch (error) {
            console.error('Error fetching individual record:', error);
            return { name: 'Unknown Individual', avatarURL: null }; // Handle error case
        }
    };

    useEffect(() => {
        const fetchNames = async () => {
            const fetchedIndividuals = [];
            for (const individual of asset.assetIndividuals) {
                const individualRecord = await fetchIndividualFullName(
                    individual.id,
                );
                fetchedIndividuals.push(individualRecord);
            }
            setSharedIndividuals(fetchedIndividuals);
        };

        if (user?.jwt.ind === asset.individualId) {
            fetchNames();
        }
    }, [asset.assetIndividuals, user]);

    return (
        <>
            <div
                className={styles.readOnlyItem}
                style={{ marginBottom: '16px' }}
            >
                <p>{getText('msg_709a23220f2c')}</p>
                <p>{asset.name}</p>
            </div>

            {asset.assetTypeId === 4 && (
                <div
                    className={styles.readOnlyItem}
                    style={{ marginBottom: '16px' }}
                >
                    <p>{getText('msg_80843a89b396')}</p>
                    <p>{formatDateToMMDDYYYY(asset.createdAt)}</p>
                </div>
            )}

            {asset.isGlobalAsset ? (
                <div style={{ paddingLeft: '8px' }}>
                    <div className={styles.readOnlyHistoryItem}>
                        <p>{getText('msg_1910e1df6652')}</p>
                        <p>{asset.description}</p>
                    </div>
                    <div className={styles.readOnlyHistoryItem}>
                        <p>{getText('msg_848eed0fbd54')}</p>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: '3px',
                                width: '100%',
                                flexWrap: 'wrap',
                            }}
                        >
                            {asset.assetTags.map((tag, index) => (
                                <span key={index} className={styles.tag}>
                                    {tag.tag ?? tag}
                                </span>
                            ))}
                            {asset.assetTags?.length < 1 && (
                                <p
                                    style={{
                                        fontStyle: 'italic',
                                    }}
                                >
                                    {getText('msg_b88ef5aa4d2f')}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    <div
                        className={styles.readOnlyItem}
                        style={{ marginBottom: '16px' }}
                    >
                        <p>{getText('msg_accf40c89baa')}</p>
                        <p>{formatDateToMMDDYYYY(asset.createdAt)}</p>
                    </div>
                    <div
                        className={styles.readOnlyItem}
                        style={{ marginBottom: '16px' }}
                    >
                        <p>{getText('msg_43de2bcd6337')}</p>
                        <div
                            className="d-flex"
                            style={{ gap: '10px', alignItems: 'center' }}
                        >
                            <Avatar src={asset.avatarURL ?? ''} />
                            <p>{asset.createdBy}</p>
                        </div>
                    </div>
                    {user?.jwt.ind !== asset.individualId && (
                        <div
                            className={styles.readOnlyItem}
                            style={{ marginBottom: '16px' }}
                        >
                            <p>{getText('msg_0c79adcf41ec')}</p>
                            <p>{formatDateToMMDDYYYY(asset.updatedAt)}</p>
                        </div>
                    )}
                </>
            )}

            <div className={styles.readOnlyItem}>
                {user?.jwt.ind === asset.individualId ? (
                    <>
                        <p>{getText('msg_21d816e75550')}</p>
                        {sharedIndividuals.length > 0 ? (
                            sharedIndividuals.map((individual, index) => (
                                <div
                                    className="d-flex"
                                    style={{
                                        gap: '10px',
                                        alignItems: 'center',
                                        marginTop: '10px',
                                    }}
                                >
                                    <Avatar
                                        src={individual.avatarURL ?? ''}
                                    ></Avatar>
                                    <p key={index}>{individual.name}</p>
                                </div>
                            ))
                        ) : (
                            <p>
                                {getText('msg_5734e5e69adb')}
                            </p>
                        )}
                    </>
                ) : (
                    <>{/* Additional Content Could go here */}</>
                )}
            </div>
        </>
    );
};

export default AssetReadOnlyPanel;
